import { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from '@/hooks/use-toast';

type Period = 'month' | 'quarter' | 'year' | 'custom';

interface GenerateFirmReportDialogProps {
  firmId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PERIOD_LABELS: Record<Period, string> = {
  month: 'Previous month',
  quarter: 'Previous quarter',
  year: 'Previous year',
  custom: 'Custom date range',
};

export function GenerateFirmReportDialog({
  firmId,
  open,
  onOpenChange,
}: GenerateFirmReportDialogProps) {
  const [period, setPeriod] = useState<Period>('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const apiBase = (import.meta as any).env?.VITE_API_URL || 'https://go.sentra.be';

  const handleGenerate = async () => {
    if (period === 'custom' && (!startDate || !endDate)) {
      toast({
        title: 'Date range required',
        description: 'Please pick both a start and end date for a custom range.',
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom' && startDate && endDate) {
        params.set('start_date', startDate);
        params.set('end_date', endDate);
      }
      const url = `${apiBase}/api/firms/${firmId}/report-pdf/?${params.toString()}`;
      const token = localStorage.getItem('accessToken');
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let detail = '';
        try {
          const j = await res.json();
          detail = j.detail || '';
        } catch {
          // ignore
        }
        throw new Error(detail || `Request failed (${res.status})`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Could not generate report',
        description: err instanceof Error ? err.message : 'Unknown error.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Firm Report
          </DialogTitle>
          <DialogDescription>
            Produce a comprehensive PDF report for the whole firm covering
            matters, lawyers and invoicing for the chosen period.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="period">Reporting period</Label>
            <Select
              value={period}
              onValueChange={(v) => setPeriod(v as Period)}
            >
              <SelectTrigger id="period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                  <SelectItem key={p} value={p}>
                    {PERIOD_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {period === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start date</Label>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="Pick a date"
                />
              </div>
              <div className="space-y-2">
                <Label>End date</Label>
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate || undefined}
                  placeholder="Pick a date"
                />
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            The report is styled to match the matter-level report (navy and
            gold, Times typeface). It will open in a new browser tab once
            generated.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleGenerate} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                Generate PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
